import React, { useState } from 'react';
import { useI18nContext } from '@procore/core-react';
import Box from '@mui/material/Box';
import { useStyles } from './ProjectMetaData.styles';
import { type SvgIconProps, Typography } from '@mui/material';

export type ProfileInfoView = 'serp' | 'profile' | 'homepage';
type ProjectMetaDataProps = {
  'data-test-id'?: string;
  view: ProfileInfoView;
  Icon: React.FC<SvgIconProps>;
  items: Array<any>;
  separator?: string;
  visibleOn?: Array<ProfileInfoView>;
  chopIndex?: number;
  alignItems?: 'flex-start' | 'center' | 'flex-end';
  suppressHydrationWarning?: boolean;
};

export default function ProjectMetaData({
  'data-test-id': dataTestId,
  Icon,
  items,
  separator,
  view,
  visibleOn = ['serp', 'profile'],
  chopIndex = 0,
  alignItems = 'center',
  suppressHydrationWarning = false,
}: ProjectMetaDataProps) {
  const [showMore, setShowMore] = useState(false);
  const classes = useStyles();
  const I18n = useI18nContext();
  const maxItems = chopIndex || 20;

  if (!items?.length || !visibleOn.includes(view)) {
    return null;
  }

  const itemsToShow =
    view === 'profile' && !showMore ? items.slice(0, maxItems) : items;
  const hasMoreThanMax = view === 'profile' && items.length > maxItems;
  if (chopIndex && hasMoreThanMax) {
    itemsToShow.push('...');
  }
  return (
    <Box data-test-id={dataTestId || 'root'}>
      <Box display="flex" alignItems={alignItems} gap={1}>
        <Icon style={{ width: 18, height: 18 }} viewBox="0 0 18 18" />

        <Box
          className={view === 'serp' ? classes.ellipsis : undefined}
          data-test-id="items-container"
        >
          <Typography
            data-test-id="item-text"
            variant="projectMetaDataText"
            suppressHydrationWarning={suppressHydrationWarning}
          >
            {itemsToShow.join(separator || ', ')}
          </Typography>
          {!chopIndex && (
            <>
              {hasMoreThanMax && !showMore && (
                <Box
                  data-test-id="show-more-link"
                  className={classes.showMoreOrLess}
                  onClick={() => {
                    setShowMore(true);
                  }}
                >
                  <Typography color="gray50" data-test-id="show-more-link">
                    {I18n.t('components.business_metadata.show_more', {
                      amount: items.length - maxItems,
                    })}
                  </Typography>
                </Box>
              )}
              {hasMoreThanMax && showMore && (
                <Box
                  className={classes.showMoreOrLess}
                  data-test-id="show-less-link"
                  onClick={() => {
                    setShowMore(false);
                  }}
                >
                  <Typography color="gray50">
                    {I18n.t('components.business_metadata.show_less')}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
